import React, { useState } from 'react';
import { trackEvent } from '../hooks/useAnalytics';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { useTranslation } from '../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InquiryForm({ propertyId, apartmentId, propertyName, listingId }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', message: '', website: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.message.trim().length < 10) {
      toast.error(t('form.min_length'));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, {
        name: formData.name, email: formData.email, phone: formData.phone,
        message: formData.message, property_id: propertyId || null,
        apartment_id: apartmentId || null, listing_id: listingId || null, website: formData.website
      });
      setSuccess(true);
      toast.success(t('form.success_msg'));
      setFormData({ name: '', email: '', phone: '', message: '', website: '' });
      trackEvent('inquiry_submit', { property_id: propertyId || 'general', listing_id: listingId || 'general' });
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error(t('form.too_many'));
      } else if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error(t('form.error'));
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="text-center py-8" data-testid="inquiry-success">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="font-heading font-semibold text-xl mb-2">{t('form.success_title')}</h3>
        <p className="text-muted-foreground mb-4">{t('form.success_msg')}</p>
        <Button variant="outline" onClick={() => setSuccess(false)}>{t('form.new_inquiry')}</Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="inquiry-form">
      {propertyName && (
        <p className="text-sm text-muted-foreground mb-4">
          {t('form.inquiry_for')}: <span className="font-medium text-foreground">{propertyName}</span>
        </p>
      )}
      <div>
        <Label htmlFor="name">{t('form.name')} *</Label>
        <Input id="name" type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder={t('form.placeholder_name')} data-testid="inquiry-name-input" />
      </div>
      <div>
        <Label htmlFor="email">{t('form.email')} *</Label>
        <Input id="email" type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder={t('form.placeholder_email')} data-testid="inquiry-email-input" />
      </div>
      <div>
        <Label htmlFor="phone">{t('form.phone')} *</Label>
        <Input id="phone" type="tel" required value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder={t('form.placeholder_phone')} data-testid="inquiry-phone-input" />
      </div>
      <div>
        <Label htmlFor="message">{t('form.message')} *</Label>
        <Textarea id="message" required rows={4} value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} placeholder={t('form.placeholder_message')} data-testid="inquiry-message-input" />
      </div>
      <div style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }} aria-hidden="true">
        <label htmlFor="website">Website</label>
        <input id="website" type="text" name="website" tabIndex={-1} autoComplete="off" value={formData.website} onChange={(e) => setFormData({ ...formData, website: e.target.value })} />
      </div>
      <Button type="submit" className="w-full" disabled={loading} data-testid="inquiry-submit-btn">
        {loading ? (<><div className="spinner w-4 h-4 mr-2" />{t('form.sending')}</>) : (<><Send className="w-4 h-4 mr-2" />{t('form.submit')}</>)}
      </Button>
    </form>
  );
}
